/* eslint-disable no-undef */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { usePrevious } from 'react-use';

import KeyCodeConstants from '../../../KeyCodeConstants';
import isEqual from 'lodash/isEqual';
import ListItem from './ListItem';

import Input from '@zg-rentals/multibrand-ui/lib/Input';

import './style.scss';

function Autocomplete({ itemList, placeholder, value: listingValue, onChange, onMouseDown, onTriggerAction }) {
  const initialValues = {
    displayItemList: false,
    itemList: itemList,
    selectedIndex: -1,
    acTimeout: null,
  };

  const [stateAutocomplete, setStateAutocomplete] = useState(initialValues);

  const prevItemList = usePrevious(itemList);

  useEffect(() => {
    if (itemList.length === 0) {
      setStateAutocomplete((prevState) => {
        return {
          ...prevState,
          displayItemList: false,
        };
      });
    } else if (prevItemList && (prevItemList.length === 0 || !isEqual(itemList, prevItemList))) {
      setStateAutocomplete((prevState) => {
        return {
          ...prevState,
          itemList: itemList,
          displayItemList: true,
          selectedIndex: 0,
        };
      });
    }
  }, [itemList]);

  const initAutocompleteState = () => {
    setStateAutocomplete((prevState) => {
      return {
        ...prevState,
        displayItemList: false,
        selectedIndex: -1,
      };
    });
  };

  const decrementSelectedIndex = (val) => {
    setStateAutocomplete((prevState) => {
      return {
        ...prevState,
        selectedIndex: val < 0 ? itemList.length - 1 : val,
      };
    });
  };

  const incrementSelectedIndex = (val) => {
    setStateAutocomplete((prevState) => {
      return {
        ...prevState,
        selectedIndex: val > itemList.length - 1 ? 0 : val,
      };
    });
  };

  const isValidString = (str) => {
    // string valid length > minKeyStroke
    const minKeystrokes = 6;
    str = str.trim();
    return str.length >= minKeystrokes;
  };

  const onChangeHandler = (event) => {
    const onChangeCallbackDelay = 300;
    const val = event.target.value;
    const { acTimeout } = stateAutocomplete;

    // update state in parent component
    onChange(val);

    if (val === '') {
      initAutocompleteState();
    }

    if (isValidString(val)) {
      // throttle response
      clearTimeout(acTimeout);
      setStateAutocomplete((prevState) => {
        return {
          ...prevState,
          acTimeout: setTimeout(() => {
            onTriggerAction();
          }, onChangeCallbackDelay),
        };
      });
    }
  };

  const onMouseDownHandler = (item) => {
    initAutocompleteState();
    onMouseDown(item.name);
  };

  const onBlur = () => {
    initAutocompleteState();
  };

  const onKeyDown = (event) => {
    const { selectedIndex } = stateAutocomplete;
    const { keyCode } = event;

    if (!itemList.length) {
      return;
    }

    if (keyCode === KeyCodeConstants.ENTER) {
      if (selectedIndex !== -1) {
        onMouseDownHandler(itemList[selectedIndex]);
      } else {
        onMouseDownHandler(itemList[0]);
      }
    }

    if (keyCode === KeyCodeConstants.TAB) {
      initAutocompleteState();
    }

    if (keyCode === KeyCodeConstants.DOWN_ARROW) {
      incrementSelectedIndex(selectedIndex + 1);
    }

    if (keyCode === KeyCodeConstants.UP_ARROW) {
      decrementSelectedIndex(selectedIndex - 1);
    }
  };

  const { displayItemList, selectedIndex } = stateAutocomplete;

  return (
    <div>
      <div>
        <Input
          type="text"
          placeholder={placeholder}
          label={''}
          value={listingValue}
          onBlur={onBlur}
          onChange={onChangeHandler}
          onKeyDown={onKeyDown}
          error={''}
        />
      </div>

      {displayItemList && (
        <div className="autocomplete-dropdown">
          {itemList.map((item, index) => {
            const key = item.id || index;
            return <ListItem key={key} item={item} onMouseDown={onMouseDownHandler} hover={selectedIndex === index} />;
          })}
        </div>
      )}
    </div>
  );
}

Autocomplete.propTypes = {
  itemList: PropTypes.arrayOf(PropTypes.object),
  value: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onMouseDown: PropTypes.func,
  onTriggerAction: PropTypes.func,
};

export default Autocomplete;
